import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Gift, Search, Copy, CheckCircle2, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const CAT_TONE = {
  food: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  fitness: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  wellness: "border-teal-500/40 text-teal-300 bg-teal-500/10",
  services: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  retail: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  beauty: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  auto: "border-slate-500/40 text-slate-300 bg-slate-500/10",
  petcare: "border-yellow-500/40 text-yellow-300 bg-yellow-500/10",
  home: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  entertainment: "border-pink-500/40 text-pink-300 bg-pink-500/10",
  other: "border-white/10 text-zinc-400",
};

const discountLabel = (p) => {
  if (p.discount_type === "percent" && p.discount_value) return `${Math.round(p.discount_value)}% off`;
  if (p.discount_type === "flat" && p.discount_value) return `$${Math.round(p.discount_value)} off`;
  if (p.discount_type === "bogo") return "BOGO";
  if (p.discount_type === "freebie") return "Free gift";
  return "Special offer";
};

export default function ResidentPerksPage() {
  const [perks, setPerks] = useState([]);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [claimed, setClaimed] = useState({});
  const [claimDialog, setClaimDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: mine }] = await Promise.all([
        api.get("/perks", { params: category !== "all" ? { category } : {} }),
        api.get("/me/perks/claimed"),
      ]);
      setPerks(data.items || []);
      const claimedMap = {};
      (mine.items || []).forEach((c) => { claimedMap[c.perk_id] = c; });
      setClaimed(claimedMap);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/resident/ResidentPerksPage.jsx: swallowed', e); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const filtered = perks.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (p.title?.toLowerCase().includes(q)
            || p.business_name?.toLowerCase().includes(q)
            || p.description?.toLowerCase().includes(q));
  });

  const claim = async (p) => {
    try {
      const { data } = await api.post(`/perks/${p.id}/claim`);
      setClaimDialog({ perk: p, ...data });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Claim failed"); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Copied");
  };

  return (
    <div>
      <PageHeader
        eyebrow="Perks"
        title="Resident Perks"
        subtitle="Exclusive discounts from local businesses — verified for your community. Claim a perk to lock in your code."
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 pr-3">
          <Search className="h-3 w-3 text-zinc-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search perks…"
                 className="h-8 border-0 bg-transparent focus-visible:ring-0 text-sm w-56"
                 data-testid="perks-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44 bg-white/[0.04] border-white/10" data-testid="perks-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All categories</SelectItem>
            {Object.keys(CAT_TONE).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500">{filtered.length} live perk{filtered.length === 1 ? "" : "s"}</div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-white/10" data-testid="perks-empty">
          <Gift className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-zinc-400">No live perks match right now. Check back soon — your admin is lining up more.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <Card key={p.id} className="p-4 border-white/10 bg-card flex flex-col" data-testid={`perk-${p.id}`}>
              <div className="flex items-start gap-3 mb-2">
                {p.logo_url ? (
                  <img src={p.logo_url} alt={p.business_name}
                       className="h-10 w-10 rounded-md object-cover border border-white/10" />
                ) : (
                  <div className="h-10 w-10 rounded-md bg-gradient-to-br from-orange-500/20 to-orange-700/40 flex items-center justify-center">
                    <Gift className="h-5 w-5 text-orange-300" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-100 truncate">{p.title}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{p.business_name}</div>
                </div>
                <Badge variant="outline" className={CAT_TONE[p.category] || CAT_TONE.other}
                       data-testid={`perk-cat-${p.id}`}>
                  {discountLabel(p)}
                </Badge>
              </div>
              {p.description && <div className="text-xs text-zinc-400 line-clamp-3 mb-2">{p.description}</div>}
              {p.address && (
                <div className="text-[11px] text-zinc-500 flex items-center gap-1 mb-2">
                  <MapPin className="h-3 w-3" /> {p.address}
                </div>
              )}
              <div className="mt-auto flex items-center gap-1.5">
                {claimed[p.id] ? (
                  <Button size="sm" variant="outline"
                          className="border-emerald-500/40 text-emerald-300 rounded-full flex-1"
                          onClick={() => setClaimDialog({ perk: p, code: claimed[p.id].code_snapshot,
                                                            already_claimed: true, claim: claimed[p.id] })}
                          data-testid={`perk-view-${p.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> View my code
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => claim(p)}
                          className="bg-orange-500 hover:bg-orange-600 rounded-full flex-1"
                          data-testid={`perk-claim-${p.id}`}>
                    <Gift className="h-3 w-3 mr-1" /> Claim
                  </Button>
                )}
                {p.redeem_url && (
                  <a href={p.redeem_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5"
                     data-testid={`perk-link-${p.id}`}>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              {p.expires_at && (
                <div className="text-[10px] text-zinc-500 mt-2">Expires {new Date(p.expires_at).toLocaleDateString()}</div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!claimDialog} onOpenChange={(v) => !v && setClaimDialog(null)}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[440px]" data-testid="perk-claim-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">
              {claimDialog?.already_claimed ? "Your code" : "Perk claimed 🎉"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs text-center">
              {claimDialog?.perk?.business_name} · {claimDialog?.perk?.title}
            </DialogDescription>
          </DialogHeader>
          {claimDialog?.code ? (
            <div className="rounded-lg border-2 border-dashed border-orange-500/40 bg-orange-500/[0.05] p-4 text-center">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Promo code</div>
              <div className="font-mono text-2xl font-bold text-orange-300 tracking-wide" data-testid="perk-claim-code">
                {claimDialog.code}
              </div>
              <Button size="sm" onClick={() => copyCode(claimDialog.code)}
                      variant="outline"
                      className="mt-3 border-white/10 rounded-full"
                      data-testid="perk-claim-copy">
                <Copy className="h-3 w-3 mr-1" /> Copy code
              </Button>
            </div>
          ) : (
            <div className="text-sm text-zinc-300 text-center py-2">
              Show this claim to the business at checkout.
            </div>
          )}
          {claimDialog?.perk?.redeem_instructions && (
            <div className="text-xs text-zinc-400 whitespace-pre-wrap">
              {claimDialog.perk.redeem_instructions}
            </div>
          )}
          {claimDialog?.perk?.redeem_url && (
            <a href={claimDialog.perk.redeem_url} target="_blank" rel="noreferrer"
               className="text-center text-xs text-orange-300 hover:underline"
               data-testid="perk-claim-redeem-link">
              Open the business page →
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
